import { useState, useEffect, useCallback } from "react";
import { Paper, Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Edit as EditIcon } from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import { useNavigate } from "react-router-dom";
import { listarInquilinosMOBI, IInquilinoMOBI } from "services/modules/liberacoes/mobi";
import { IconButton, Tooltip } from "@mui/material";

/**
 * Tela de Listagem de Clientes CEM MOBI
 * Exibe todos os clientes que possuem contratado o CEM MOBI (versão web)
 * Ao clicar em um cliente, redireciona para a tela de liberações daquele cliente
 */
export const LiberacoesMOBI = () => {
  const { openNotification } = useNavigation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [clientes, setClientes] = useState<IInquilinoMOBI[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Filtros por coluna
  const [filterNomeEmpresa, setFilterNomeEmpresa] = useState("");
  const [filterCNPJ, setFilterCNPJ] = useState("");
  const [filterUF, setFilterUF] = useState("");
  const [filterCidade, setFilterCidade] = useState("");

  /**
   * Carrega a lista de clientes CEM MOBI (inquilinos)
   */
  const carregarClientes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listarInquilinosMOBI({
        pageIndex: page - 1,
        pageSize,
      });

      setClientes(response.dados || []);
      // Usar paginas * pageSize para calcular total aproximado, ou usar length dos dados
      setTotalRecords(response.dados?.length || 0);

      if ((response as any).aviso) {
        openNotification({
          message: (response as any).aviso,
          type: "warning",
        });
      }
    } catch (error) {
      openNotification({
        message: "Erro ao carregar clientes CEM MOBI",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, openNotification]);

  useEffect(() => {
    carregarClientes();
  }, [carregarClientes]);

  /**
   * Ao clicar no botão de editar, redireciona para tela de liberações do cliente
   */
  const handleEditarLiberacoes = (inquilinoId: string, nomeInquilino: string) => {
    navigate(`/liberacoesMobiMof/${inquilinoId}`, {
      state: { nomeInquilino } 
    });
  };

  /**
   * Colunas da tabela (seguindo mesmo padrão de Liberações CEM DELPHI)
   */
  const columns: GridColDef[] = [
    {
      field: "razaoSocial",
      headerName: "Nome da empresa",
      flex: 1.5,
      minWidth: 200,
      renderHeader: () => (
        <FilterColumn
          label="Nome da empresa"
          name="razaoSocial"
          onChange={(e) => setFilterNomeEmpresa(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.razaoSocial || params.row.nomeFantasia || "-",
    },
    {
      field: "nomeFantasia",
      headerName: "Nome fantasia",
      flex: 1.5,
      minWidth: 200,
      renderHeader: () => (
        <FilterColumn
          label="Nome fantasia"
          name="nomeFantasia"
          onChange={(e) => setFilterNomeEmpresa(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.nomeFantasia || params.row.razaoSocial || "-",
    },
    {
      field: "uf",
      headerName: "UF",
      flex: 0.5,
      minWidth: 80,
      renderHeader: () => (
        <FilterColumn
          label="UF"
          name="uf"
          onChange={(e) => setFilterUF(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.estado || "-",
    },
    {
      field: "cidade",
      headerName: "Cidade",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Cidade"
          name="cidade"
          onChange={(e) => setFilterCidade(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.cidade || "-",
    },
    {
      field: "cnpj",
      headerName: "CNPJ",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="CNPJ"
          name="cnpj"
          onChange={(e) => setFilterCNPJ(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.documento || "-",
    },
    {
      field: "acoes",
      headerName: "",
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Gerenciar Liberações">
          <IconButton
          size="small"
            color="primary"
            onClick={() =>
              handleEditarLiberacoes(
                params.row.id,
                params.row.razaoSocial || params.row.nomeFantasia
              )
            }
        >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Filtrar clientes localmente
  const clientesFiltrados = clientes.filter((cliente) => {
    const nomeMatch = !filterNomeEmpresa || 
      (cliente.razaoSocial?.toLowerCase().includes(filterNomeEmpresa.toLowerCase()) ||
       cliente.nomeFantasia?.toLowerCase().includes(filterNomeEmpresa.toLowerCase()));
    
    const cnpjMatch = !filterCNPJ || 
      cliente.documento?.includes(filterCNPJ);

    const ufMatch = !filterUF || 
      cliente.estado?.toLowerCase().includes(filterUF.toLowerCase());

    const cidadeMatch = !filterCidade || 
      cliente.cidade?.toLowerCase().includes(filterCidade.toLowerCase());

    return nomeMatch && cnpjMatch && ufMatch && cidadeMatch;
  });

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="LIBERAÇÕES CEM MOBI"
        label="Listagem de clientes com CEM MOBI contratado"
        hasBackButton={false}
      />

      <Paper elevation={3}>
        <Box sx={{ p: 2 }}>
          <TitleTable title="Listagem de clientes CEM Mobi" />
            <StripedDataGrid
            columns={columns}
            rows={clientesFiltrados}
              getRowId={(row) => row.id}
              loading={isLoading}
            page={page - 1}
            pageSize={pageSize}
            rowCount={totalRecords}
            onPageChange={(newPage: number) => setPage(newPage + 1)}
            onPageSizeChange={(newPageSize: number) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
              checkboxSelection={false}
            />
          </Box>
      </Paper>

      <BackdropLoading isOpen={isLoading} />
    </DashboardStackCustom>
  );
};
